import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export type ThemeMode = 'light' | 'dark' | 'system';

export const useThemeStore = defineStore('theme', () => {
  const mode = ref<ThemeMode>((localStorage.getItem('themeMode') as ThemeMode) || 'system');
  const sidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true');

  function initTheme(): void {
    applyTheme();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', () => {
      if (mode.value === 'system') {
        applyTheme();
      }
    });
  }

  function applyTheme(): void {
    const isDark =
      mode.value === 'dark' ||
      (mode.value === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

    document.documentElement.classList.toggle('dark', isDark);
  }

  function setMode(newMode: ThemeMode): void {
    mode.value = newMode;
    localStorage.setItem('themeMode', newMode);
    applyTheme();
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value;
    localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value));
  }

  function setSidebarCollapsed(collapsed: boolean): void {
    sidebarCollapsed.value = collapsed;
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }

  watch(mode, () => {
    applyTheme();
  });

  return {
    mode,
    sidebarCollapsed,
    initTheme,
    setMode,
    toggleSidebar,
    setSidebarCollapsed,
  };
});
